import { SearchOutlined } from "@ant-design/icons";
import { BriefDeliverableTaskUploadTypeEnum } from "@entities/brief/lib/constants";
import { useUploadTaskContent } from "@entities/brief/lib/hooks/useUploadTaskContent";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { ToastService } from "@shared/lib/services";
import { ICampaignDeliverableTaskContent } from "@shared/lib/types/briefCampains";
import { Button, Divider, Form, Input } from "@shared/ui";
import { LinkPreview } from "@shared/ui/LinkPreview";
import { Link2 } from "lucide-react";
import { useEffect, useMemo } from "react";
import styles from "./styles.module.scss";

interface AddBriefCampaignLiveProps {
  onClose: () => void;
  taskId: string;
  campaignInfluencerId: string;
  deliverableConfigRefId: string;
}

type LiveFormValues = {
  link: string;
  commentsFromCreator?: string;
};

const isValidUrl = (value?: string) => {
  if (!value?.trim()) return false;

  try {
    new URL(value.trim());
    return true;
  } catch {
    return false;
  }
};

export function AddBriefCampaignLive({
  onClose,
  taskId,
  campaignInfluencerId,
  deliverableConfigRefId,
}: AddBriefCampaignLiveProps) {
  const { briefId } = useBriefStore();
  const [form] = Form.useForm<LiveFormValues>();
  const linkUrl: string | undefined = Form.useWatch("link", form);
  const { uploadTaskContent, isUploading, isUploaded } =
    useUploadTaskContent(briefId);

  const showPreview = useMemo(() => isValidUrl(linkUrl), [linkUrl]);

  const onFinish = (values: LiveFormValues) => {
    const url = values.link?.trim();
    if (!isValidUrl(url)) {
      ToastService.error("Not a valid URL");
      return;
    }

    const contentUrls: ICampaignDeliverableTaskContent[] = [
      { url: url!, type: BriefDeliverableTaskUploadTypeEnum.MediaLink },
    ];

    uploadTaskContent({
      data: {
        campaignInfluencerId,
        deliverableConfigRefId,
        taskId,
        contentUrls,
      },
    });
  };

  useEffect(() => {
    if (isUploaded) {
      ToastService.success("Live link submitted");
      onClose();
    }
  }, [isUploaded]);

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={{ link: "" }}
      className={styles.form}
    >
      <div className={styles.content}>
        <div className={styles.inputSection}>
          <div className={styles.sectionHeader}>
            <h4 className={styles.sectionTitle}>Live Link</h4>
            <p className={styles.sectionDescription}>
              Paste the unlisted URL of the content that has gone live. A
              preview will appear once the link is valid.
            </p>
          </div>

          <Form.Item
            name="link"
            label="Post URL"
            className={styles.formItem}
            rules={[
              { required: true, message: "Link is required" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (isValidUrl(value)) return Promise.resolve();
                  return Promise.reject("Not a valid URL");
                },
              },
            ]}
          >
            <Input
              placeholder="https://instagram.com/reel/..."
              className={styles.input}
              suffix={<SearchOutlined className={styles.inputIcon} />}
            />
          </Form.Item>

          <p className={styles.hint}>
            Supports Instagram, YouTube, LinkedIn, and other public post links.
          </p>
        </div>

        <div className={styles.previewSection}>
          <span className={styles.previewLabel}>Preview</span>
          <div className={styles.previewContainer}>
            {showPreview ? (
              <div className={styles.previewContent}>
                <LinkPreview url={linkUrl!.trim()} />
              </div>
            ) : (
              <div className={styles.previewEmpty}>
                <div className={styles.previewEmptyIcon}>
                  <Link2 size={24} />
                </div>
                <p className={styles.previewEmptyTitle}>No preview yet</p>
                <p className={styles.previewEmptyDescription}>
                  Enter a valid post URL on the left to see how the live content
                  will appear.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Divider className={styles.divider} />

      <div className={styles.footer}>
        <Button onClick={onClose} className={styles.cancelButton}>
          Cancel
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          loading={isUploading}
          disabled={isUploading}
          className={styles.saveButton}
        >
          Save
        </Button>
      </div>
    </Form>
  );
}
